import { useRef, useState, useCallback } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { Typewriter } from "./Typewriter";
import type { TypewriterSegment } from "./Typewriter";
import { WordCycler } from "./WordCycler";
import { Phone3D } from "./Phone3D";
import { HOME_SAMPLE_REPORT_ID } from "./homeSample";
import { LazyNarrativeFrame } from "./LazyNarrativeFrame";

const DEMO_URL = import.meta.env.DEV
  ? `/?report=${HOME_SAMPLE_REPORT_ID}`
  : `${window.location.origin}/?report=${HOME_SAMPLE_REPORT_ID}`;
const DEMO_EMBED_URL = `${DEMO_URL}&format=video`;

const HERO_SEGMENTS: TypewriterSegment[] = [
  "Your customers ",
  "\n",
  "deserve their own ",
  "\n",
  { text: "Year in Review.", className: "italic text-fwd-yellow" },
];

const CADENCE_WORDS = ["Quarter", "Month", "Campaign", "Year"];

/** Floating particles for the hero navy background */
const HeroParticles = () => {
  const reducedMotion = useReducedMotion();
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 20 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: 2 + (i % 4) * 2.5,
            height: 2 + (i % 4) * 2.5,
            left: `${(i * 13 + 5) % 95}%`,
            top: `${(i * 19 + 8) % 90}%`,
            opacity: reducedMotion ? 0.3 : undefined,
            background:
              i % 3 === 0
                ? "rgba(255,77,46,0.4)"
                : i % 3 === 1
                  ? "rgba(255,225,77,0.2)"
                  : "rgba(255,255,255,0.12)",
          }}
          animate={
            reducedMotion
              ? undefined
              : {
                  y: [0, -35 - (i % 4) * 18, 0],
                  x: [0, i % 2 === 0 ? 12 : -12, 0],
                  opacity: [0.3, 0.75, 0.3],
                }
          }
          transition={
            reducedMotion
              ? undefined
              : {
                  duration: 3.5 + (i % 4) * 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.35,
                }
          }
        />
      ))}
    </div>
  );
};

/** Slow-moving gradient mesh for hero */
const HeroGradientMesh = () => {
  const reducedMotion = useReducedMotion();
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute w-[800px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,77,46,0.35) 0%, rgba(255,77,46,0.12) 40%, transparent 70%)",
          top: "-20%",
          left: "-15%",
        }}
        animate={reducedMotion ? undefined : { x: [0, 60, 0], y: [0, 40, 0] }}
        transition={
          reducedMotion
            ? undefined
            : { duration: 10, repeat: Infinity, ease: "easeInOut" }
        }
      />
      <motion.div
        className="absolute w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,225,77,0.22) 0%, rgba(255,225,77,0.06) 40%, transparent 70%)",
          bottom: "-15%",
          right: "-10%",
        }}
        animate={reducedMotion ? undefined : { x: [0, -40, 0], y: [0, -30, 0] }}
        transition={
          reducedMotion
            ? undefined
            : { duration: 13, repeat: Infinity, ease: "easeInOut" }
        }
      />
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)",
          top: "40%",
          left: "30%",
        }}
        animate={
          reducedMotion ? undefined : { x: [0, 30, -20, 0], y: [0, -25, 15, 0] }
        }
        transition={
          reducedMotion
            ? undefined
            : { duration: 18, repeat: Infinity, ease: "easeInOut" }
        }
      />
    </div>
  );
};

export const LandingHero = () => {
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [typewriterDone, setTypewriterDone] = useState(false);
  const onTypewriterComplete = useCallback(() => setTypewriterDone(true), []);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={sectionRef}
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 pt-(--site-header-h,4rem)"
    >
      {/* Left — navy */}
      <div className="bg-fwd-navy relative overflow-hidden flex flex-col justify-center px-8 md:px-16 lg:px-20 py-20 lg:py-0 min-h-[85vh] lg:min-h-0">
        {/* Background grid */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="heroGrid"
              x="0"
              y="0"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#heroGrid)" />
        </svg>
        {/* Animated background effects */}
        <HeroGradientMesh />
        <HeroParticles />

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-fwd-red text-xs font-semibold tracking-[0.2em] uppercase mb-8 flex items-center gap-3"
        >
          <span className="w-6 h-px bg-fwd-red" />
          FORWARD
        </motion.div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="font-display text-[clamp(2.8rem,5vw,5.5rem)] font-normal leading-[1.05] text-white mb-8 tracking-tight min-h-[4.2em] md:min-h-[3.15em]"
        >
          {!typewriterDone ? (
            <Typewriter
              segments={HERO_SEGMENTS}
              onComplete={onTypewriterComplete}
              cursorClass="bg-fwd-yellow"
            />
          ) : (
            <>
              {"Your customers "}
              <br className="hidden md:block" />
              {"deserve their own "}
              <br className="hidden md:block" />
              <span className="italic text-fwd-yellow">
                <WordCycler
                  words={CADENCE_WORDS}
                  className="text-fwd-yellow"
                  cursorClass="bg-fwd-yellow"
                  initialWord="Year"
                  startDelay={1200}
                />{" "}
                in Review.
              </span>
            </>
          )}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={typewriterDone ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-white/60 text-lg md:text-xl max-w-md leading-relaxed font-light mb-10"
        >
          Celebrated outcomes, not just clicks. Personalized narratives built
          from the data you already have, at any scale, with no technical lift.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={typewriterDone ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="flex flex-wrap items-center gap-4"
        >
          <a
            href="#get-started"
            className="bg-fwd-red text-white text-sm font-semibold tracking-wider uppercase px-8 py-4 rounded hover:opacity-90 transition-opacity"
          >
            Get free mockup
          </a>
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/70 text-sm font-medium border-b border-white/25 pb-1 hover:text-fwd-yellow hover:border-fwd-yellow transition-colors"
          >
            Try Example&nbsp;&rarr;
          </a>
        </motion.div>
      </div>

      {/* Right — soft cream with phone mockup (3D tilt on hover) */}
      <div className="bg-fwd-soft relative overflow-hidden flex flex-col items-center justify-center px-8 py-16 lg:py-0 min-h-[560px]">
        {/* Decorative watermark */}
        <div
          aria-hidden="true"
          className="absolute bottom-[-40px] right-[-20px] font-display text-[18rem] font-normal text-fwd-ink/5 leading-none pointer-events-none select-none tracking-tighter hidden lg:block"
        >
          YiR
        </div>

        {/* Phone — parallax on scroll, float continuously, 3D tilt on mouse */}
        <motion.div style={{ y: phoneY }} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <motion.div
              animate={reducedMotion ? undefined : { y: [0, -8, 0] }}
              transition={
                reducedMotion
                  ? undefined
                  : {
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2,
                    }
              }
            >
              <Phone3D href={DEMO_URL}>
                <div className="w-[350px] md:w-[375px] bg-fwd-ink rounded-[44px] p-4 shadow-[0_30px_80px_rgba(17,17,24,0.22),0_0_0_1px_rgba(17,17,24,0.08)]">
                  <div className="w-16 h-1.5 bg-white/5 rounded-full mx-auto mb-3" />
                  <div className="rounded-[30px] overflow-hidden h-[600px] md:h-[650px] bg-[#0a0a12] relative">
                    <LazyNarrativeFrame
                      src={DEMO_EMBED_URL}
                      title="Sample Year in Review narrative"
                      className="w-full h-full"
                      interactive={false}
                    />
                    {/* Touch/scroll blocker over iframe */}
                    <div className="absolute inset-0 z-10" />
                  </div>
                </div>
                {/* Tag */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-fwd-red text-white text-[0.65rem] font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full whitespace-nowrap z-30">
                  Live Example
                </div>
              </Phone3D>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
